import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import SupervisorMonthlyReports from './SupervisorMonthlyReports';
import TeacherDayStartTab from '../../components/Teacher/TeacherDayStartTab';
import StudentAssessmentViewDialog from '../../components/Teacher/StudentAssessmentViewDialog';
import StudentAssessmentEditDialog from '../../components/Teacher/StudentAssessmentEditDialog';
import ParentReportSnapshotView from '../../components/Parent/ParentReportSnapshotView';
import { useAuth } from '../../contexts/AuthContext';

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function StudentAssessmentInbox() {
  const { hasRole } = useAuth();
  const isReadOnlyAgent = hasRole('parent_feedback_agent');
  const isAssignedRm = hasRole(['sales', 'sales_supervisor']);
  const readOnly = isReadOnlyAgent || isAssignedRm;
  const now = new Date();
  // Current month — teachers submit at month-end; supervisors review/approve in the same month.
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);
  const [statusFilter, setStatusFilter] = useState('all');
  const [teacherId, setTeacherId] = useState('');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [viewReportId, setViewReportId] = useState(null);
  const [editReportId, setEditReportId] = useState(null);
  const [comment, setComment] = useState('');
  const queryClient = useQueryClient();

  const teachersQuery = useQuery(
    ['viewableTeachersForAssessmentInbox'],
    async () => {
      const res = await teacherModuleAPI.getViewableTeachers();
      return res.data?.data?.teachers || [];
    },
    { staleTime: 5 * 60_000, enabled: !isAssignedRm }
  );

  const listQuery = useQuery(
    ['studentAssessmentSubmissions', month, statusFilter, teacherId],
    async () => {
      const res = await teacherModuleAPI.listStudentAssessmentSubmissions({
        month,
        status: statusFilter || 'all',
        teacherId: teacherId || undefined,
      });
      return res.data?.data || [];
    },
    { enabled: !!month }
  );

  const teacherOptions = useMemo(() => {
    const fromApi = (teachersQuery.data || []).map((t) => {
      const id = String(t.id);
      const name = `${t.first_name || ''} ${t.last_name || ''}`.trim();
      return { id, label: name || t.email || id };
    });
    if (fromApi.length) {
      return fromApi.sort((a, b) => a.label.localeCompare(b.label));
    }
    // Fallback: teachers present in the current result set
    const map = new Map();
    for (const row of listQuery.data || []) {
      if (!row.teacherId) continue;
      const id = String(row.teacherId);
      if (!map.has(id)) map.set(id, { id, label: row.teacherName || id });
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [teachersQuery.data, listQuery.data]);

  const filteredRows = useMemo(() => {
    const q = teacherSearch.trim().toLowerCase();
    if (!q) return listQuery.data || [];
    return (listQuery.data || []).filter((row) => {
      const teacher = String(row.teacherName || '').toLowerCase();
      const student = String(row.studentName || '').toLowerCase();
      const batch = String(row.batchName || '').toLowerCase();
      return teacher.includes(q) || student.includes(q) || batch.includes(q);
    });
  }, [listQuery.data, teacherSearch]);

  const reviewMutation = useMutation(
    ({ id, action, comment: c }) => teacherModuleAPI.reviewStudentAssessment(id, { action, comment: c }),
    {
      onSuccess: (_data, vars) => {
        toast.success(vars?.action === 'reopened' ? 'Approval cancelled' : 'Review saved');
        setReviewTarget(null);
        setComment('');
        queryClient.invalidateQueries(['studentAssessmentSubmissions']);
        queryClient.invalidateQueries(['studentAssessmentSubmission']);
        queryClient.invalidateQueries(['parentMonthlyReportsQueue']);
        queryClient.invalidateQueries(['studentMonthlyReportDetail']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Review failed'),
    }
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const m = shiftMonth(defaultMonth, -i);
            return (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All generated</MenuItem>
          <MenuItem value="submitted">Submitted</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Teacher"
          value={teacherId}
          onChange={(e) => setTeacherId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All teachers</MenuItem>
          {teacherOptions.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Search"
          placeholder="Teacher, student, or batch"
          value={teacherSearch}
          onChange={(e) => setTeacherSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Stack>
      {listQuery.isLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Approved by</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.studentName}</TableCell>
                  <TableCell>{row.teacherName}</TableCell>
                  <TableCell>{row.batchName}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === 'approved'
                          ? 'success'
                          : row.status === 'rejected'
                            ? 'error'
                            : 'info'
                      }
                    />
                  </TableCell>
                  <TableCell>{row.status === 'approved' ? row.approvedBy || '—' : '—'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setViewReportId(row.id)}>
                      View
                    </Button>
                    {!readOnly && row.status === 'submitted' ? (
                      <>
                        <Button size="small" color="success" sx={{ ml: 1 }} onClick={() => setReviewTarget({ ...row, action: 'approved' })}>
                          Approve
                        </Button>
                        <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => setReviewTarget({ ...row, action: 'rejected' })}>
                          Reject
                        </Button>
                      </>
                    ) : null}
                    {!readOnly && row.status === 'approved' ? (
                      <>
                        <Button size="small" sx={{ ml: 1 }} onClick={() => setEditReportId(row.id)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="warning"
                          sx={{ ml: 1 }}
                          onClick={() => {
                            setComment('');
                            setReviewTarget({ ...row, action: 'reopened' });
                          }}
                        >
                          Cancel approval
                        </Button>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {!filteredRows.length ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    No student assessments match these filters. Approved reports show under All
                    generated or Status = Approved — also check the month.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!reviewTarget} onClose={() => setReviewTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewTarget?.action === 'approved'
            ? 'Approve report'
            : reviewTarget?.action === 'reopened'
              ? 'Cancel approval'
              : 'Reject report'}
        </DialogTitle>
        <DialogContent>
          {reviewTarget?.action === 'reopened' && (
            <Typography sx={{ mt: 1 }}>
              This puts <strong>{reviewTarget.studentName}</strong>&apos;s report back to Submitted.
              Approve and Reject show again, and the teacher can update it. An email already sent to
              the parent stays sent.
            </Typography>
          )}
          {reviewTarget?.action === 'rejected' && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Remarks (optional)"
              placeholder="Optional note for the teacher"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={reviewTarget?.action === 'reopened' ? 'warning' : 'primary'}
            disabled={reviewMutation.isLoading}
            onClick={() =>
              reviewMutation.mutate({
                id: reviewTarget.id,
                action: reviewTarget.action,
                comment,
              })
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <StudentAssessmentViewDialog
        open={!!viewReportId}
        reportId={viewReportId}
        onClose={() => setViewReportId(null)}
      />
      <StudentAssessmentEditDialog
        open={!!editReportId}
        reportId={editReportId}
        onClose={() => setEditReportId(null)}
      />
    </Box>
  );
}

function statusChipColor(status) {
  if (status === 'sent') return 'success';
  if (status === 'ready') return 'success';
  return 'warning';
}

function statusChipLabel(status) {
  if (status === 'sent') return 'sent';
  if (status === 'ready') return 'ready to send';
  return 'blocked';
}

function ParentReportQueue() {
  const { hasRole } = useAuth();
  const isReadOnlyAgent = hasRole('parent_feedback_agent');
  const isAssignedRm = hasRole(['sales', 'sales_supervisor']);
  const readOnly = isReadOnlyAgent || isAssignedRm;
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);
  const [teacherId, setTeacherId] = useState('');
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewWarnings, setPreviewWarnings] = useState([]);
  const queryClient = useQueryClient();

  const teachersQuery = useQuery(
    ['viewableTeachersForParentQueue'],
    async () => {
      const res = await teacherModuleAPI.getViewableTeachers();
      return res.data?.data?.teachers || [];
    },
    { staleTime: 5 * 60_000, enabled: !isAssignedRm }
  );

  const listQuery = useQuery(
    ['parentMonthlyReportsQueue', month, teacherId],
    async () => {
      const res = await teacherModuleAPI.listParentMonthlyReports({
        month,
        status: 'queue',
        teacherId: teacherId || undefined,
      });
      return res.data?.data || [];
    },
    { enabled: !!month }
  );

  const teacherOptions = useMemo(() => {
    const fromApi = (teachersQuery.data || []).map((t) => {
      const id = String(t.id);
      const name = `${t.first_name || ''} ${t.last_name || ''}`.trim();
      return { id, label: name || t.email || id };
    });
    if (fromApi.length) return fromApi.sort((a, b) => a.label.localeCompare(b.label));
    const map = new Map();
    for (const row of listQuery.data || []) {
      if (!row.teacherId) continue;
      const id = String(row.teacherId);
      if (!map.has(id)) map.set(id, { id, label: row.teacherName || id });
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [teachersQuery.data, listQuery.data]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return listQuery.data || [];
    return (listQuery.data || []).filter((row) => {
      const teacher = String(row.teacherName || '').toLowerCase();
      const student = String(row.studentName || '').toLowerCase();
      const email = String(row.parentEmail || '').toLowerCase();
      return teacher.includes(q) || student.includes(q) || email.includes(q);
    });
  }, [listQuery.data, search]);

  const sendMutation = useMutation(
    ({ studentId, reportMonth }) => teacherModuleAPI.sendParentMonthlyReport(studentId, reportMonth),
    {
      onSuccess: () => {
        toast.success('Sent to parent');
        queryClient.invalidateQueries(['parentMonthlyReportsQueue']);
        setPreview(null);
        setPreviewWarnings([]);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Send failed'),
    }
  );

  const previewMutation = useMutation(
    ({ studentId, reportMonth }) => teacherModuleAPI.getParentMonthlyReport(studentId, reportMonth),
    {
      onSuccess: (res) => {
        setPreview(res.data?.data?.snapshot);
        setPreviewWarnings(res.data?.data?.warnings || []);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Preview failed'),
    }
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const m = shiftMonth(defaultMonth, -i);
            return (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          select
          size="small"
          label="Teacher"
          value={teacherId}
          onChange={(e) => setTeacherId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All teachers</MenuItem>
          {teacherOptions.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Search"
          placeholder="Teacher, student, or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        Rows appear when the student assessment is approved. Send stays manual and requires teacher
        achievement approved + parent email on file.
      </Alert>
      {listQuery.isLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Approved by</TableCell>
                <TableCell>Warnings</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => {
                const canSend = row.canSend === true;
                const warnings = row.warnings || [];
                return (
                  <TableRow key={row.id}>
                    <TableCell>{row.studentName}</TableCell>
                    <TableCell>{row.teacherName || '—'}</TableCell>
                    <TableCell>{row.parentEmail || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={statusChipLabel(row.status)} color={statusChipColor(row.status)} />
                    </TableCell>
                    <TableCell>{row.approvedBy || '—'}</TableCell>
                    <TableCell>
                      {warnings.length ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {warnings.map((w) => (
                            <Chip key={w} size="small" color="warning" variant="outlined" label={w} />
                          ))}
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() =>
                          previewMutation.mutate({ studentId: row.studentId, reportMonth: row.reportMonth })
                        }
                      >
                        Preview
                      </Button>
                      {!readOnly ? (
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ ml: 1 }}
                          disabled={!canSend}
                          title={canSend ? undefined : warnings.join('; ') || 'Send gates not met'}
                          onClick={() =>
                            sendMutation.mutate({ studentId: row.studentId, reportMonth: row.reportMonth })
                          }
                        >
                          {row.status === 'sent' ? 'Resend' : 'Send'}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!filteredRows.length ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No parent reports match these filters.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={!!preview}
        onClose={() => {
          setPreview(null);
          setPreviewWarnings([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Parent report preview</DialogTitle>
        <DialogContent>
          {preview && (
            <Box>
              {previewWarnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {previewWarnings.join('; ')}
                </Alert>
              )}
              <ParentReportSnapshotView snapshot={preview} compact />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPreview(null);
              setPreviewWarnings([]);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function SupervisorMonthlyReportsHub() {
  const { hasRole } = useAuth();
  const isReadOnlyAgent = hasRole('parent_feedback_agent');
  const hideTeacherTab = isReadOnlyAgent || hasRole(['sales', 'sales_supervisor']);
  const showDayStart = hasRole(['admin', 'teacher_admin', 'teacher_supervisor']);
  const [tab, setTab] = useState(
    isReadOnlyAgent ? 'parents' : hideTeacherTab ? 'assessments' : 'achievements'
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Monthly reports
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {!hideTeacherTab ? <Tab value="achievements" label="Teacher achievements" /> : null}
        {showDayStart ? <Tab value="dayStart" label="Day start" /> : null}
        <Tab value="assessments" label="Student assessments" />
        <Tab value="parents" label="Ready for parents" />
      </Tabs>
      {!hideTeacherTab && tab === 'achievements' && <SupervisorMonthlyReports embedded />}
      {showDayStart && tab === 'dayStart' && <TeacherDayStartTab />}
      {tab === 'assessments' && <StudentAssessmentInbox />}
      {tab === 'parents' && <ParentReportQueue />}
    </Box>
  );
}
